import React, { useState, useEffect } from "react";
import {
    Table,
    Button,
    Space,
    Image,
    Switch,
    message,
    Modal,
    Form,
    Input,
    Upload,
    Breadcrumb,
    Popconfirm,
    InputNumber,
    Select,
} from "antd";
import {
    PlusOutlined,
    UploadOutlined,
    ArrowLeftOutlined,
    HomeOutlined,
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addProduct, updateProduct, deleteProduct, deleteService, fetchServices } from "../../store/slices/servicesSlice";
import noImage from "../../assets/images/no-image.png";

const ServiceList = () => {
    const { categoryId, subCategoryId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { services, loading } = useSelector((state) => state.services);

    const [parentService, setParentService] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingSubService, setEditingSubService] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        dispatch(fetchServices({categoryId, subCategoryId}));
    }, [dispatch, categoryId, subCategoryId]);

    // Helper function to strip uploads prefix from image path (for sending to backend)
    const stripUploadsPrefix = (imagePath) => {
        if (!imagePath || typeof imagePath !== 'string') return imagePath;
        if (imagePath.startsWith('/uploads/service/')) {
            return imagePath.replace('/uploads/service/', '');
        }
        if (imagePath.startsWith('/uploads/')) {
            return imagePath.replace('/uploads/', '');
        }
        return imagePath;
    };

    // Helper function to normalize image path: convert /uploads/ to /uploads/products/
    const normalizeImagePath = (imagePath) => {
        if (!imagePath || typeof imagePath !== 'string') return imagePath;
        if (imagePath.startsWith('/uploads/') && !imagePath.startsWith('/uploads/service/')) {
            return imagePath.replace('/uploads/', '/uploads/service/');
        }
        return imagePath;
    };

    // Helper function to get full image URL
    const getImageUrl = (image) => {
        if (!image) return noImage;
        if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:') || image.startsWith('blob:')) {
            return image;
        }
        const normalizedPath = normalizeImagePath(image);
        if (normalizedPath.startsWith('/uploads/service/')) {
            const baseURL = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api').replace('/api', '');
            return `${baseURL}${normalizedPath}`;
        }
        const baseURL = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api').replace('/api', '');
        const imagePath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
        return `${baseURL}/uploads/service${imagePath}`;
    };

    // Toggle Active/Inactive
    const handleToggle = async (id, checked) => {
        try {
            const service = services?.data?.find((p) => p._id === id);
            if (!service) return;

            const updated = { ...service, status: checked ? "1" : "0" };
            await dispatch(updateProduct({ id, data: updated })).unwrap();
            message.success(`Product ${checked ? "activated" : "deactivated"} successfully`);
        } catch {
            message.error("Failed to update status");
        }
    };

    const handleAddSubService = () => {
        setEditingSubService(null);
        setIsSubmitting(false);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEditSubService = (record) => {
        setEditingSubService(record);
        setIsSubmitting(false);
        setIsModalVisible(true);

        // Construct full image URL for display in edit form
        const imageUrl = record.image ? getImageUrl(record.image) : null;

        form.setFieldsValue({
            name: record.title,
            description: record.description,
            price: record.price,
            type: record.type,
            status: record.status === "1",
            image: imageUrl
                ? [
                    {
                        uid: "-1",
                        name: "no-img.png",
                        status: "done",
                        url: imageUrl, // full URL for display
                    },
                ]
                : [],
        });
    };

    const handleDeleteService = async (id) => {
        try {
            await dispatch(deleteService(id)).unwrap();
            await dispatch(fetchServices({categoryId, subCategoryId}))
            message.success("Product deleted successfully");
        } catch {
            message.error("Failed to delete product");
        }
    };

    const handleModalOk = async () => {
        if (isSubmitting) return;
        try {
            const values = await form.validateFields();
            let imageFile = null;

            if (values.image && values.image.length > 0) {
                const fileItem = values.image[0];
                if (fileItem.originFileObj) {
                    imageFile = fileItem.originFileObj; // new upload
                } else if (fileItem.url) {
                    // Extract filename from full URL or path
                    let imagePath = fileItem.url;
                    // If it's a full URL, extract the path part
                    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
                        try {
                            const url = new URL(imagePath);
                            imagePath = url.pathname;
                        } catch (e) {
                            // If URL parsing fails, use as is
                        }
                    }
                    // Strip /uploads/ or /uploads/products/ prefix if present, keep only filename
                    imageFile = stripUploadsPrefix(imagePath);
                    // Remove leading slash if still present
                    if (imageFile.startsWith('/')) {
                        imageFile = imageFile.substring(1);
                    }
                }
            }

            const payload = {
                categoryId,
                subCategoryId,
                price : values?.price,
                title: values.name,
                description: values.description,
                type: values.type,
                status: values.status ? "1" : "0",
                image: imageFile,
            };

            setIsSubmitting(true);
            try {
                if (editingSubService) {
                    await dispatch(updateProduct({ id: editingSubService._id, data: payload })).unwrap();
                    message.success("Product updated successfully");
                } else {
                    await dispatch(addProduct(payload)).unwrap();
                    await dispatch(fetchServices({categoryId, subCategoryId}))
                    message.success("Product added successfully");
                }
            } catch (apiError) {
                const errorMessage = typeof apiError === 'string'
                        ? apiError
                        : apiError?.response?.data?.message || apiError?.message || 'Failed to add service';
                    message.error(errorMessage);
            } finally {
                setIsSubmitting(false);
            }

            setIsModalVisible(false);
            setEditingSubService(null);
            form.resetFields();
        } catch (err) {
            console.error(err);
            message.error("Failed to save product");
        }
    };

    const beforeUpload = (file) => {
        const isImage = file.type.startsWith("image/");
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isImage) message.error("You can only upload images!");
        if (!isLt2M) message.error("Image must be smaller than 2MB!");
        return false;
    };

    const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

    const columns = [
        {
            title: "Image",
            dataIndex: "image",
            key: "image",
            width: 120,
            render: (image) => (
                <Image
                    src={getImageUrl(image)}
                    width={80}
                    height={80}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                />
            ),
        },
        { title: "Title", dataIndex: "title", key: "title" },
        { title: "Description", dataIndex: "description", key: "description" },
        { title: "Service Type", dataIndex: "type", key: "type" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status, record) => (
                <Switch
                    checked={status === "1"}
                    onChange={(checked) => handleToggle(record._id, checked)}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                />
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => handleEditSubService(record)}>
                        Edit
                    </Button>
                    <Popconfirm title="Delete this product?" onConfirm={() => handleDeleteService(record._id)}>
                        <Button icon={<DeleteOutlined />} size="small" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // if (!parentService) return <div>Loading...</div>;

    return (
        <div>
            {/* <Breadcrumb
                style={{ marginBottom: 16 }}
                items={[
                    { title: <Space onClick={() => navigate("/services")}><HomeOutlined /> Services</Space> },
                    { title: parentService.name },
                ]}
            /> */}

            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                    Back to Sub Categories
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddSubService}>
                    Add Service
                </Button>
            </div>

            <Table columns={columns} dataSource={services?.data} rowKey="_id" loading={loading} pagination={{ pageSize: 10 }} />

            <Modal
                title={editingSubService ? "Edit Service" : "Add Service"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
                okText={editingSubService ? "Update" : "Add"}
                confirmLoading={isSubmitting}
                okButtonProps={{ disabled: isSubmitting }}
            >
                <Form form={form} layout="vertical" initialValues={{ status: true }}>
                    <Form.Item label="Service Name" name="name" rules={[{ required: true, message: "Please enter service name!" }]}>
                        <Input placeholder="Enter service name" />
                    </Form.Item>
                    <Form.Item label="Description" name="description" rules={[{ required: true, message: "Please enter description!" }]}>
                        <Input placeholder="Enter description" />
                    </Form.Item>
                    <Form.Item label="Price" name="price" rules={[{ required: true, message: "Please enter price!" }]}>
                        <InputNumber placeholder="Enter price" style={{ width : "100%" }} />
                    </Form.Item>
                    <Form.Item
                        label="Service Type"
                        name="type"
                        rules={[{ required: true, message: "Select service type" }]}
                    >
                        <Select
                        placeholder="Select service type"
                        options={[
                            { value: 'simple', label : 'Simple' },
                            { value: 'complex', label : 'Complex' }
                        ]}
                        />
                    </Form.Item>

                    <Form.Item label="Product Image" name="image" valuePropName="fileList" getValueFromEvent={normFile}>
                        <Upload listType="picture-card" beforeUpload={beforeUpload} maxCount={1}>
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>
                        </Upload>
                    </Form.Item>

                    <Form.Item label="Status" name="status" valuePropName="checked">
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ServiceList;